import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../config/firebase";

const TechPrepShowcaseFormB = () => {
  const { showcaseId } = useParams();
  const recaptchaRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [docId, setDocId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    project_title: "",
    oversized_project: "no",
    electricity_required: "no",
    attending_in_person: "yes",
    team_member_1: "",
    team_member_2: "",
    team_member_3: "",
  });
  const [readOnlyData, setReadOnlyData] = useState({
    tech_prep_SOD: "",
    Instructor_ID: "",
    competitive_category: "",
  });
  const [existingPaperUrl, setExistingPaperUrl] = useState("");

  const [file, setFile] = useState(null);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const loadSubmission = async () => {
      if (!showcaseId) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      try {
        const q = query(
          collection(db, "ShowcaseSubmissions"),
          where("showcase_ID_string", "==", showcaseId),
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const submissionDoc = snap.docs[0];
        const data = submissionDoc.data();
        setDocId(submissionDoc.id);
        setFormData({
          project_title: data.project_title || "",
          oversized_project: data.oversized_project || "no",
          electricity_required: data.electricity_required || "no",
          attending_in_person: data.attending_in_person || "yes",
          team_member_1: data.team_member_1 || "",
          team_member_2: data.team_member_2 || "",
          team_member_3: data.team_member_3 || "",
        });
        setReadOnlyData({
          tech_prep_SOD: data.tech_prep_SOD || "",
          Instructor_ID: data.Instructor_ID || "",
          competitive_category: data.competitive_category || "",
        });
        setExistingPaperUrl(data.medical_bioscience_paper || "");
      } catch (err) {
        console.error("Error loading showcase submission:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadSubmission();
  }, [showcaseId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    if (selected.type !== "application/pdf") {
      setErrors((prev) => ({ ...prev, file: "Please upload a PDF file." }));
      setFile(null);
      e.target.value = "";
      return;
    }
    setErrors((prev) => ({ ...prev, file: "" }));
    setFile(selected);
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!file) newErrors.file = "Medical Bioscience Paper (PDF) is required.";
    if (!captchaValue)
      newErrors.captcha = "Please complete the CAPTCHA verification.";
    if (!confirmed)
      newErrors.confirm =
        "Please confirm you've reviewed the information above.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const storageRef = ref(
        storage,
        `tpshowcase/${showcaseId}_${file.name}`,
      );
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const docRef = doc(db, "ShowcaseSubmissions", docId);
      await updateDoc(docRef, {
        ...formData,
        medical_bioscience_paper: downloadURL,
        app_B_submitted: -1,
        updated_stamp: new Date().toISOString(),
      });

      setExistingPaperUrl(downloadURL);
      setSubmitted(true);
      setSnackbar({
        open: true,
        message: "Medical Bioscience Paper submitted successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error submitting medical bioscience paper:", err);
      setSnackbar({
        open: true,
        message: "Error submitting. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box mt={8} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (notFound) {
    return (
      <Container maxWidth="md">
        <Box mt={8}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Submission Not Found
            </Typography>
            <Typography variant="body1" align="center">
              We couldn't find a Tech Prep Showcase submission for this link.
              Please check the link from your email, or contact{" "}
              <a href="mailto:techprep@sinclair.edu">techprep@sinclair.edu</a>{" "}
              for assistance.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={8} mb={4} display="flex" flexDirection="column" alignItems="center">
        <Paper elevation={3} sx={{ width: "100%", p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom color="primary">
            Medical Bioscience Paper Submission
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="textSecondary"
            paragraph
          >
            Review your team's information below, upload your Medical
            Bioscience paper, and submit.
          </Typography>

          {submitted ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Your Medical Bioscience Paper has been submitted successfully.
              {existingPaperUrl && (
                <>
                  {" "}
                  <a href={existingPaperUrl} target="_blank" rel="noreferrer">
                    View uploaded file
                  </a>
                </>
              )}
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <TextField
                  fullWidth
                  label="Project Title"
                  name="project_title"
                  value={formData.project_title}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 3,
                  }}
                >
                  <FormControl component="fieldset" disabled sx={{ flex: 1 }}>
                    <FormLabel component="legend">Oversized Project</FormLabel>
                    <RadioGroup
                      row
                      name="oversized_project"
                      value={formData.oversized_project}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" disabled sx={{ flex: 1 }}>
                    <FormLabel component="legend">
                      Electricity Required
                    </FormLabel>
                    <RadioGroup
                      row
                      name="electricity_required"
                      value={formData.electricity_required}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" disabled sx={{ flex: 1 }}>
                    <FormLabel component="legend">
                      Attending In Person
                    </FormLabel>
                    <RadioGroup
                      row
                      name="attending_in_person"
                      value={formData.attending_in_person}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Medical Bioscience Paper (PDF) *
                  </Typography>
                  {existingPaperUrl && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Current file on record:{" "}
                      <a
                        href={existingPaperUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View uploaded file
                      </a>
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                  >
                    {file ? "Change PDF File" : "Choose PDF File"}
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>

                  {file && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1.5,
                        p: 1,
                        pl: 1.5,
                        border: "1px solid",
                        borderColor: "success.main",
                        borderRadius: 1,
                        backgroundColor: "#dcfce7",
                        maxWidth: "fit-content",
                      }}
                    >
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({formatFileSize(file.size)})
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Remove selected file"
                        onClick={handleRemoveFile}
                        sx={{ ml: 0.5 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  {errors.file && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {errors.file}
                    </Typography>
                  )}
                </Box>

                <Divider />

                <Alert severity="info">
                  Important: Teams must have 2-5 student members. Teamwork is
                  a key factor in judging.
                </Alert>

                <TextField
                  fullWidth
                  label="Team Member 1"
                  name="team_member_1"
                  value={formData.team_member_1}
                  onChange={handleChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Team Member 2"
                  name="team_member_2"
                  value={formData.team_member_2}
                  onChange={handleChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Team Member 3"
                  name="team_member_3"
                  value={formData.team_member_3}
                  onChange={handleChange}
                  variant="outlined"
                />

                <Divider />

                <TextField
                  fullWidth
                  label="Site / School"
                  value={readOnlyData.tech_prep_SOD}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  fullWidth
                  label="Instructor"
                  value={readOnlyData.Instructor_ID}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  fullWidth
                  label="Competitive Category"
                  value={readOnlyData.competitive_category}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={
                      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                      "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                    }
                    onChange={(value) => {
                      setCaptchaValue(value);
                      if (errors.captcha) {
                        setErrors((prev) => ({ ...prev, captcha: "" }));
                      }
                    }}
                    onExpired={() => setCaptchaValue(null)}
                    onErrored={() => setCaptchaValue(null)}
                  />
                </Box>
                {errors.captcha && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", textAlign: "center" }}
                  >
                    {errors.captcha}
                  </Typography>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      onChange={(e) => {
                        setConfirmed(e.target.checked);
                        if (errors.confirm) {
                          setErrors((prev) => ({ ...prev, confirm: "" }));
                        }
                      }}
                    />
                  }
                  label="Check this box once you've reviewed all of the above and are ready to submit."
                />
                {errors.confirm && (
                  <Typography variant="caption" color="error">
                    {errors.confirm}
                  </Typography>
                )}

                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!confirmed || submitting}
                    sx={{ minWidth: 200, py: 1.5 }}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default TechPrepShowcaseFormB;
